import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PendingVerifications from "../helpers/pendingVerifications";
import {Link} from 'react-router-dom';
import DeclinedProviders from "../helpers/declinedVerification";

interface TabItem {
  label: string;
  count: number | null;
}

/**
 * Verification queues.
 *
 * Freelancers and Businesses used to be separate tabs. They are one queue split
 * by account type, and splitting it hid work: Businesses sat on 0 while someone
 * still had to remember to look. They are merged into Pending, which shows the
 * account type as a column instead.
 *
 * The Pending badge is reported BY the list rather than fetched alongside it.
 * The two used to be independent requests, which is how the panel came to show
 * "Freelancers 5" directly above "NO VERIFICATION REQUESTS": the badge read
 * `totalRecords` from one response and the list read `results` from another,
 * and nothing reconciled them.
 */
const VerificationContent = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [declinedCount, setDeclinedCount] = useState<number | null>(null);

  const fetchCount = async (endpoint: string): Promise<number | null> => {
    const AuthToken = localStorage.getItem("AuthToken");
    try {
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${AuthToken}` },
      });
      const total = response?.data?.providers?.totalRecords;
      return typeof total === "number" ? total : null;
    } catch (error) {
      console.error("Error fetching verification counts:", error);
      /* null, not 0: we do not know, and showing a confident 0 for a failed
       * request is how a full queue reads as an empty one. */
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      setDeclinedCount(
        await fetchCount(
          "https://api.pureworker.com/api/admin/providers?isVerified=rejected"
        )
      );
    };
    load();
  }, []);

  /* Stable identity, or the list re-fetches on every render of this parent. */
  const onPendingLoaded = useCallback(
    ({ total }: { shown: number; total: number }) => setPendingCount(total),
    []
  );

  const tabItems: TabItem[] = [
    { label: "Pending", count: pendingCount },
    { label: "Declined", count: declinedCount },
  ];

  return (
    <div className="">
      <div className="px-3">
        <p className="text-[#000] font-roboto text-2xl font-medium leading-normal mt-4 border-b-2 border-[#000] w-fit px-4">
          Verification
        </p>
      </div>
      <p className="px-6 mt-4">Review submitted profiles here. ID approval does not approve a profile. <Link className="text-purple-800 underline" to="/verified-providers">View Verified SPs and both approval statuses</Link></p>
      <section className="flex px-2 lg:px-8 mt-6 mb-2 gap-4 lg:gap-6">
        {tabItems.map((tab, index) => (
          <p
            key={tab.label}
            className={`bg-[#fff] py-2 px-2 lg:px-6 rounded-full text-center font-outfit text-xs lg:text-sm font-medium leading-normal tracking-tighter cursor-pointer border ${
              selectedTab === index ? "border-[#88087B]" : ""
            }`}
            onClick={() => setSelectedTab(index)}
          >
            {tab.label}{" "}
            {tab.count !== null && (
              <span className="bg-[#88087B] rounded-sm text-[#fff] px-1 ml-0.5 lg:ml-2">
                {tab.count}
              </span>
            )}
          </p>
        ))}
      </section>
      <main className="px-4">
        {selectedTab === 0 && <PendingVerifications onLoaded={onPendingLoaded} />}
        {selectedTab === 1 && <DeclinedProviders />}
      </main>
    </div>
  );
};

export default VerificationContent;
